import { api } from "@repo/convex/api";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/theme-context";
import { RIGHT_SIDEBAR_MAX, RIGHT_SIDEBAR_MIN } from "@/features/project/types";
import { SUPPORTED_SPELLCHECK_LANGUAGES } from "@/features/rich-text/spellcheck";
import { SettingsPageShell } from "@/features/settings";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import {
  clampRightPanelWidth,
  emitRightPanelWidthChange,
  resolveRightPanelWidth,
  saveRightPanelWidth,
  subscribeRightPanelWidthChange,
} from "@/lib/right-panel-width";
import {
  applySidebarWidth,
  clampSidebarWidth,
  emitSidebarWidthChange,
  getCurrentSidebarWidth,
  readSavedSidebarWidth,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  saveSidebarWidth,
  subscribeSidebarWidthChange,
} from "@/lib/sidebar-width";
import { cn } from "@/lib/utils";
import {
  type AssistantAddressingPreference,
  type AssistantVerbosityPreference,
  PROFILE_DEFAULT_ASSISTANT_ADDRESSING,
  PROFILE_DEFAULT_ASSISTANT_VERBOSITY,
  PROFILE_DEFAULT_RIGHT_PANEL_WIDTH,
  PROFILE_DEFAULT_SIDEBAR_WIDTH,
  PROFILE_DEFAULT_SPELLCHECK_ENABLED,
  PROFILE_DEFAULT_SPELLCHECK_LANGUAGE,
  resolveSpellcheckLanguagePreference,
  type SpellcheckLanguagePreference,
  serializeProfilePreferences,
  type ThemePreference,
} from "./-profile-preferences";

const THEME_OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const;

export function ProfileSettingsPage() {
  const preferences = useQuery(api.userPreferences.get, {});
  const updatePreferences = useObservedMutation(api.userPreferences.update, {
    feature: "settings",
    operation: "update-profile-preferences",
    includeRequestId: true,
  });
  const { theme: activeTheme, setTheme: applyTheme } = useTheme();

  const [theme, setTheme] = useState<ThemePreference>(activeTheme);
  const [defaultSidebarWidthDraft, setDefaultSidebarWidthDraft] = useState("320");
  const [defaultRightPanelWidthDraft, setDefaultRightPanelWidthDraft] = useState(
    String(PROFILE_DEFAULT_RIGHT_PANEL_WIDTH)
  );
  const [savedDefaultSidebarWidth, setSavedDefaultSidebarWidth] = useState(
    PROFILE_DEFAULT_SIDEBAR_WIDTH
  );
  const [savedDefaultRightPanelWidth, setSavedDefaultRightPanelWidth] = useState(
    PROFILE_DEFAULT_RIGHT_PANEL_WIDTH
  );
  const [autoCollapseSidebar, setAutoCollapseSidebar] = useState(false);
  const [spellcheckEnabled, setSpellcheckEnabled] = useState(PROFILE_DEFAULT_SPELLCHECK_ENABLED);
  const [spellcheckLanguage, setSpellcheckLanguage] = useState<SpellcheckLanguagePreference>(
    PROFILE_DEFAULT_SPELLCHECK_LANGUAGE
  );
  const [assistantAddressing, setAssistantAddressing] = useState<AssistantAddressingPreference>(
    PROFILE_DEFAULT_ASSISTANT_ADDRESSING
  );
  const [assistantVerbosity, setAssistantVerbosity] = useState<AssistantVerbosityPreference>(
    PROFILE_DEFAULT_ASSISTANT_VERBOSITY
  );
  const [profileSaveState, setProfileSaveState] = useState<"idle" | "saving" | "saved" | "error">(
    "idle"
  );
  const [sidebarWidthSaveState, setSidebarWidthSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [rightPanelWidthSaveState, setRightPanelWidthSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const initializedRef = useRef(false);
  const lastHydratedPreferencesRef = useRef<string | null>(null);
  const lastSavedRef = useRef(
    serializeProfilePreferences(
      activeTheme,
      PROFILE_DEFAULT_SIDEBAR_WIDTH,
      PROFILE_DEFAULT_RIGHT_PANEL_WIDTH,
      false,
      PROFILE_DEFAULT_SPELLCHECK_ENABLED,
      PROFILE_DEFAULT_SPELLCHECK_LANGUAGE,
      PROFILE_DEFAULT_ASSISTANT_ADDRESSING,
      PROFILE_DEFAULT_ASSISTANT_VERBOSITY
    )
  );
  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (preferences === undefined) return;

    const nextSavedSidebarWidth =
      preferences?.ui?.defaultSidebarWidth ?? PROFILE_DEFAULT_SIDEBAR_WIDTH;
    const nextSavedRightPanelWidth =
      preferences?.ui?.defaultRightPanelWidth ?? PROFILE_DEFAULT_RIGHT_PANEL_WIDTH;
    const nextCurrentWidth = getCurrentSidebarWidth() ?? readSavedSidebarWidth();
    const nextCurrentRightPanelWidth = resolveRightPanelWidth(nextSavedRightPanelWidth);
    const nextAutoCollapse = Boolean(preferences?.ui?.autoCollapseSidebar);
    const nextSpellcheckEnabled =
      preferences?.spellcheck?.enabled ?? PROFILE_DEFAULT_SPELLCHECK_ENABLED;
    const nextSpellcheckLanguage = resolveSpellcheckLanguagePreference(
      preferences?.spellcheck?.language
    );
    const nextAssistantAddressing =
      preferences?.assistant?.addressing === "first_name" ? "first_name" : "neutral";
    const nextAssistantVerbosity =
      preferences?.assistant?.verbosity === "concise" ||
      preferences?.assistant?.verbosity === "detailed"
        ? preferences.assistant.verbosity
        : "balanced";
    const hydrationKey = JSON.stringify({
      theme: preferences?.theme ?? null,
      ui: {
        defaultSidebarWidth: nextSavedSidebarWidth,
        defaultRightPanelWidth: nextSavedRightPanelWidth,
        autoCollapseSidebar: nextAutoCollapse,
      },
      spellcheck: {
        enabled: nextSpellcheckEnabled,
        language: nextSpellcheckLanguage,
      },
      assistant: {
        addressing: nextAssistantAddressing,
        verbosity: nextAssistantVerbosity,
      },
    });

    if (hydrationKey === lastHydratedPreferencesRef.current) {
      return;
    }

    setTheme(activeTheme);
    setDefaultSidebarWidthDraft(String(nextCurrentWidth));
    setDefaultRightPanelWidthDraft(String(nextCurrentRightPanelWidth));
    setSavedDefaultSidebarWidth(nextSavedSidebarWidth);
    setSavedDefaultRightPanelWidth(nextSavedRightPanelWidth);
    setAutoCollapseSidebar(nextAutoCollapse);
    setSpellcheckEnabled(nextSpellcheckEnabled);
    setSpellcheckLanguage(nextSpellcheckLanguage);
    setAssistantAddressing(nextAssistantAddressing);
    setAssistantVerbosity(nextAssistantVerbosity);
    lastHydratedPreferencesRef.current = hydrationKey;
    lastSavedRef.current = serializeProfilePreferences(
      activeTheme,
      nextSavedSidebarWidth,
      nextSavedRightPanelWidth,
      nextAutoCollapse,
      nextSpellcheckEnabled,
      nextSpellcheckLanguage,
      nextAssistantAddressing,
      nextAssistantVerbosity
    );
    initializedRef.current = true;
  }, [preferences, activeTheme]);

  // Keep Profile UI in sync with global theme changes (e.g. sidebar quick toggle).
  useEffect(() => {
    if (!initializedRef.current) return;
    if (theme !== activeTheme) {
      setTheme(activeTheme);
    }
  }, [activeTheme, theme]);

  useEffect(() => {
    return subscribeSidebarWidthChange((width) => {
      setDefaultSidebarWidthDraft(String(width));
    });
  }, []);

  useEffect(() => {
    return subscribeRightPanelWidthChange((width) => {
      setDefaultRightPanelWidthDraft(String(width));
    });
  }, []);

  useEffect(() => {
    if (!initializedRef.current) return;

    const payload = {
      theme,
      ui: {
        defaultSidebarWidth: savedDefaultSidebarWidth,
        defaultRightPanelWidth: savedDefaultRightPanelWidth,
        autoCollapseSidebar,
      },
      spellcheck: {
        enabled: spellcheckEnabled,
        language: spellcheckLanguage,
      },
      assistant: {
        addressing: assistantAddressing,
        verbosity: assistantVerbosity,
      },
    } as const;
    const serialized = serializeProfilePreferences(
      theme,
      savedDefaultSidebarWidth,
      savedDefaultRightPanelWidth,
      autoCollapseSidebar,
      spellcheckEnabled,
      spellcheckLanguage,
      assistantAddressing,
      assistantVerbosity
    );
    if (serialized === lastSavedRef.current) return;

    const timer = window.setTimeout(() => {
      setProfileSaveState("saving");
      void updatePreferences(payload)
        .then(() => {
          lastSavedRef.current = serialized;
          setProfileSaveState("saved");
          if (idleTimerRef.current) {
            window.clearTimeout(idleTimerRef.current);
          }
          idleTimerRef.current = window.setTimeout(() => {
            setProfileSaveState("idle");
            idleTimerRef.current = null;
          }, 1200);
        })
        .catch(() => {
          setProfileSaveState("error");
        });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [
    theme,
    autoCollapseSidebar,
    savedDefaultRightPanelWidth,
    savedDefaultSidebarWidth,
    spellcheckEnabled,
    spellcheckLanguage,
    assistantAddressing,
    assistantVerbosity,
    updatePreferences,
  ]);

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const parsedDraftWidth = Number.parseInt(defaultSidebarWidthDraft, 10);
  const parsedRightPanelDraftWidth = Number.parseInt(defaultRightPanelWidthDraft, 10);
  const isDraftValid =
    Number.isFinite(parsedDraftWidth) &&
    parsedDraftWidth >= SIDEBAR_MIN_WIDTH &&
    parsedDraftWidth <= SIDEBAR_MAX_WIDTH;
  const isRightPanelDraftValid =
    Number.isFinite(parsedRightPanelDraftWidth) &&
    parsedRightPanelDraftWidth >= RIGHT_SIDEBAR_MIN &&
    parsedRightPanelDraftWidth <= RIGHT_SIDEBAR_MAX;
  const isWidthDirty = isDraftValid && parsedDraftWidth !== savedDefaultSidebarWidth;
  const isRightPanelWidthDirty =
    isRightPanelDraftValid && parsedRightPanelDraftWidth !== savedDefaultRightPanelWidth;

  const saveLayoutWidths = async ({
    sidebarWidth = savedDefaultSidebarWidth,
    rightPanelWidth = savedDefaultRightPanelWidth,
    target,
  }: {
    sidebarWidth?: number;
    rightPanelWidth?: number;
    target: "sidebar" | "right-panel";
  }) => {
    const clampedSidebarWidth = clampSidebarWidth(sidebarWidth);
    const clampedRightPanelWidth = clampRightPanelWidth(rightPanelWidth);
    if (target === "sidebar") {
      setSidebarWidthSaveState("saving");
    } else {
      setRightPanelWidthSaveState("saving");
    }
    try {
      await updatePreferences({
        theme,
        ui: {
          defaultSidebarWidth: clampedSidebarWidth,
          defaultRightPanelWidth: clampedRightPanelWidth,
          autoCollapseSidebar,
        },
        spellcheck: {
          enabled: spellcheckEnabled,
          language: spellcheckLanguage,
        },
        assistant: {
          addressing: assistantAddressing,
          verbosity: assistantVerbosity,
        },
      });
      setSavedDefaultSidebarWidth(clampedSidebarWidth);
      setSavedDefaultRightPanelWidth(clampedRightPanelWidth);
      lastSavedRef.current = serializeProfilePreferences(
        theme,
        clampedSidebarWidth,
        clampedRightPanelWidth,
        autoCollapseSidebar,
        spellcheckEnabled,
        spellcheckLanguage,
        assistantAddressing,
        assistantVerbosity
      );

      if (target === "sidebar") {
        setDefaultSidebarWidthDraft(String(clampedSidebarWidth));
        applySidebarWidth(clampedSidebarWidth);
        saveSidebarWidth(clampedSidebarWidth);
        emitSidebarWidthChange(clampedSidebarWidth);
        setSidebarWidthSaveState("saved");
        window.setTimeout(() => setSidebarWidthSaveState("idle"), 1400);
      } else {
        setDefaultRightPanelWidthDraft(String(clampedRightPanelWidth));
        saveRightPanelWidth(clampedRightPanelWidth);
        emitRightPanelWidthChange(clampedRightPanelWidth);
        setRightPanelWidthSaveState("saved");
        window.setTimeout(() => setRightPanelWidthSaveState("idle"), 1400);
      }
    } catch {
      if (target === "sidebar") {
        setSidebarWidthSaveState("error");
      } else {
        setRightPanelWidthSaveState("error");
      }
    }
  };

  return (
    <SettingsPageShell
      title="Preferences"
      description="Manage personal display preferences for your workspace profile experience."
    >
      {/* Theme section */}
      <div className="flex flex-col gap-10 border-b border-border py-6 md:flex-row md:gap-10">
        <div className="flex flex-col gap-1 md:w-60 md:shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Theme</h2>
          <p className="text-xs leading-relaxed text-neutral-dim">
            Select a color scheme for the application. System will follow your operating system
            preference.
          </p>
        </div>
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex gap-3 flex-wrap">
            {THEME_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => {
                    setTheme(option.value);
                    applyTheme(option.value);
                  }}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-2 border p-3 transition-colors",
                    isActive
                      ? "border-primary border-2 bg-surface-blue"
                      : "border-border hover:bg-muted/50"
                  )}
                >
                  <Icon
                    className={cn("size-5", isActive ? "text-blue-content" : "text-neutral-dim")}
                  />
                  <span
                    className={cn(
                      "text-xs",
                      isActive ? "font-medium text-blue-content" : "text-muted-foreground"
                    )}
                  >
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Layout section */}
      <div className="flex flex-col gap-10 py-6 md:flex-row md:gap-10">
        <div className="flex flex-col gap-1 md:w-60 md:shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Layout</h2>
          <p className="text-xs leading-relaxed text-neutral-dim">
            Configure how the project side rails behave and appear in your workspace.
          </p>
        </div>
        <div className="flex flex-col gap-5 flex-1">
          {/* Left sidebar default width row */}
          <div className="hidden items-center justify-between md:flex">
            <span className="text-[13px] font-medium text-foreground">Left sidebar default</span>
            <div className="flex items-center gap-2">
              <Input
                id="defaultSidebarWidth"
                type="number"
                value={defaultSidebarWidthDraft}
                onChange={(event) => setDefaultSidebarWidthDraft(event.target.value)}
                min={SIDEBAR_MIN_WIDTH}
                max={SIDEBAR_MAX_WIDTH}
                className="w-16 text-right text-xs"
              />
              <span className="text-xs text-neutral-dim">px</span>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  saveLayoutWidths({ sidebarWidth: parsedDraftWidth, target: "sidebar" })
                }
                disabled={!isWidthDirty}
              >
                Save
              </Button>
            </div>
          </div>

          {/* Right panel default width row */}
          <div className="hidden items-center justify-between md:flex">
            <span className="text-[13px] font-medium text-foreground">Right panel default</span>
            <div className="flex items-center gap-2">
              <Input
                id="defaultRightPanelWidth"
                type="number"
                value={defaultRightPanelWidthDraft}
                onChange={(event) => setDefaultRightPanelWidthDraft(event.target.value)}
                min={RIGHT_SIDEBAR_MIN}
                max={RIGHT_SIDEBAR_MAX}
                className="w-16 text-right text-xs"
              />
              <span className="text-xs text-neutral-dim">px</span>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  saveLayoutWidths({
                    rightPanelWidth: parsedRightPanelDraftWidth,
                    target: "right-panel",
                  })
                }
                disabled={!isRightPanelWidthDirty}
              >
                Save
              </Button>
            </div>
          </div>

          {/* Auto-collapse row */}
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium text-foreground">
              Auto-collapse when right panel opens
            </span>
            <Switch
              id="autoCollapseSidebar"
              checked={autoCollapseSidebar}
              onCheckedChange={setAutoCollapseSidebar}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-10 border-t border-border py-6 md:flex-row md:gap-10">
        <div className="flex flex-col gap-1 md:w-60 md:shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Claudia</h2>
          <p className="text-xs leading-relaxed text-neutral-dim">
            Choose how Claudia addresses you and how much detail she provides by default.
          </p>
        </div>
        <div className="flex flex-col gap-5 flex-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] font-medium text-foreground">Addressing</span>
            <Select
              value={assistantAddressing}
              onValueChange={(value) =>
                setAssistantAddressing(value === "first_name" ? "first_name" : "neutral")
              }
            >
              <SelectTrigger className="h-9 w-[210px] text-xs" aria-label="Claudia addressing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="first_name">Use my first name</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] font-medium text-foreground">Answer detail</span>
            <Select
              value={assistantVerbosity}
              onValueChange={(value) =>
                setAssistantVerbosity(
                  value === "concise" || value === "detailed" ? value : "balanced"
                )
              }
            >
              <SelectTrigger className="h-9 w-[210px] text-xs" aria-label="Claudia answer detail">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="concise">Concise</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="detailed">Detailed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Spellcheck section */}
      <div className="flex flex-col gap-10 border-t border-border py-6 md:flex-row md:gap-10">
        <div className="flex flex-col gap-1 md:w-60 md:shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Spellcheck</h2>
          <p className="text-xs leading-relaxed text-neutral-dim">
            Run local spelling checks in document editors without sending text to a server.
          </p>
        </div>
        <div className="flex flex-col gap-5 flex-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] font-medium text-foreground">Check spelling</span>
            <Switch
              id="spellcheckEnabled"
              aria-label="Check spelling"
              data-preferences-loaded={preferences !== undefined ? "true" : "false"}
              checked={spellcheckEnabled}
              onCheckedChange={setSpellcheckEnabled}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] font-medium text-foreground">English dialect</span>
            <Select
              value={spellcheckLanguage}
              onValueChange={(value) => {
                setSpellcheckLanguage(resolveSpellcheckLanguagePreference(value));
              }}
              disabled={!spellcheckEnabled}
            >
              <SelectTrigger className="h-9 w-[210px] text-xs" aria-label="English dialect">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_SPELLCHECK_LANGUAGES.map((language) => (
                  <SelectItem key={language.value} value={language.value}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Save status */}
      <div className="min-h-5">
        {sidebarWidthSaveState === "saving" && (
          <p className="text-xs text-muted-foreground">Saving left sidebar default…</p>
        )}
        {sidebarWidthSaveState === "saved" && (
          <p className="text-xs text-muted-foreground">Left sidebar default saved.</p>
        )}
        {sidebarWidthSaveState === "error" && (
          <p className="text-xs text-destructive">Could not save the left sidebar default.</p>
        )}
        {rightPanelWidthSaveState === "saving" && (
          <p className="text-xs text-muted-foreground">Saving right panel default…</p>
        )}
        {rightPanelWidthSaveState === "saved" && (
          <p className="text-xs text-muted-foreground">Right panel default saved.</p>
        )}
        {rightPanelWidthSaveState === "error" && (
          <p className="text-xs text-destructive">Could not save the right panel default.</p>
        )}
        {profileSaveState === "saving" && (
          <p className="text-xs text-muted-foreground">Saving preferences…</p>
        )}
        {profileSaveState === "saved" && (
          <p className="text-xs text-muted-foreground">Preferences saved.</p>
        )}
        {profileSaveState === "error" && (
          <p className="text-xs text-destructive">Could not save. Try again.</p>
        )}
      </div>
    </SettingsPageShell>
  );
}

export const Route = createFileRoute("/settings/profile")({
  component: ProfileSettingsPage,
});
